import React, { useCallback, useEffect, useRef } from 'react';
import { asDraggableContentListItem, useSortableListItem } from '../../../_lib/components/SortableContentList';
import { StatusInfoError } from '../../../_lib/components/StatusInfo';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { ContentHeadline } from '../../../_lib/components/ContentHeadline';
import { PageTypeRole } from '../PageTypeRoles.types';
import { ButtonMinorAction } from '../../../components/molecules/Btn/ButtonWrapper';
import { TooltipPlacement } from '../../../components/atoms/Tooltip';
import { Icons } from '../../../components/atoms/SVG';
import { ContentParagraph } from '../../../_lib/components/ContentParagraph';
import { StatusInfoModifier } from '../../../_lib/components/StatusInfo/partials/StatusInfo';
import { ContentHeadlineModifier } from '../../../_lib/components/ContentHeadline/ContentHeadline';

type FavoriteListItemProps = {
  id: string;
  role: PageTypeRole;
};

function PageTypeRoleItem({ role }: FavoriteListItemProps) {
  const { t } = useSonTranslation('PageTypeRoles');

  const { move, nextItemId, prevItemId, error, currentItemIndex } = useSortableListItem(role.id);

  const currentPosition = useRef<number>(currentItemIndex + 1);

  const moveElementUp = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (prevItemId) {
        event.preventDefault();
        event.stopPropagation();

        move(prevItemId);
      }
    },
    [move, prevItemId]
  );

  const moveElementDown = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (nextItemId) {
        event.preventDefault();
        event.stopPropagation();

        move(nextItemId);
      }
    },
    [move, nextItemId]
  );

  useEffect(() => {
    currentPosition.current = currentItemIndex + 1;
  }, [currentItemIndex]);

  const btnUpText = !prevItemId
    ? t('actions.moveUp.disabled')
    : // eslint-disable-next-line react-hooks/refs
      t('actions.moveUp.text', { position: currentPosition.current - 1 });
  const btnDownText = !nextItemId
    ? t('actions.moveDown.disabled')
    : // eslint-disable-next-line react-hooks/refs
      t('actions.moveDown.text', { position: currentPosition.current + 1 });

  return (
    <div className="page-type-roles__draggable-item">
      <div className="page-type-roles__draggable-item-text-container">
        <ContentHeadline title={role.name} hierarchy={4} modifier={ContentHeadlineModifier.TOP_HEADLINE} />
        <ContentParagraph>{role.description}</ContentParagraph>
      </div>
      <div className="page-type-roles__draggable-item-button-container">
        <ButtonMinorAction
          text={btnUpText}
          icon={Icons.MOVE_UP}
          ariaDisabled={!prevItemId}
          onClick={moveElementUp}
          tooltipPlacement={TooltipPlacement.TOP}
        />
        <ButtonMinorAction
          text={btnDownText}
          icon={Icons.MOVE_DOWN}
          ariaDisabled={!nextItemId}
          onClick={moveElementDown}
          tooltipPlacement={TooltipPlacement.TOP}
        />
      </div>
      {error && <StatusInfoError message={t('sortErrorResponse')} modifier={StatusInfoModifier.PADDING_TOP} />}
    </div>
  );
}

export const DraggablePageTypeRoleItem = asDraggableContentListItem(PageTypeRoleItem);
